import React, { ForwardedRef, forwardRef, ReactNode, SyntheticEvent, useState } from 'react';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { metaTrigger } from '@repo/utils/helpers/keyboardHandler';
import { Pill } from '@repo/components/containers';
import styles from './ContactSearch.module.scss';
import Contact from '@repo/utils/models/Contact';
import { useContactSearch } from '@repo/hooks';
import { useSelector } from 'react-redux';
import { selectContact } from '@repo/redux/actions/contacts';
import { IDRecord } from '@repo/utils/types';

interface ContactSearchProps {
  contact?: Contact;
  disabled?: boolean;
  onContact: (contact: Contact) => void;
  onLookup: (displayName: string) => void;
  onSubmit: (e?: SyntheticEvent) => void;
  contactNotEditable?: boolean;
  className?: string;
  selectedContactDisplay?: ReactNode;
}

const ContactPill = ({
  idRecord,
  onClick,
  editable,
  className,
}: {
  idRecord: IDRecord;
  onClick?: () => void;
  editable?: boolean;
  className: string;
}) => {
  const contact = useSelector(selectContact(idRecord.id));
  return <Pill className={className} contact={contact} onClick={onClick} editable={editable} />;
};

function ContactSearchComponent(props: ContactSearchProps, ref: ForwardedRef<HTMLInputElement>) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [setContactSearchTerm, contactSearchState] = useContactSearch({
    searchAttributes: ['displayName', 'searchLookups'],
    defaultTerm: '',
  });
  const searchResults = contactSearchState.results.items.valueSeq().toArray();
  const handleRemove = () => props.onContact(null);

  const handleSelectContact = (contact) => {
    if (props.disabled) {
      return;
    }

    setContactSearchTerm('');
    setHighlightedIndex(-1);
    props.onLookup('');
    props.onContact(contact);
  };

  const moveHighlightRight = () => {
    const nextIndex = highlightedIndex + 1;
    setHighlightedIndex(nextIndex < searchResults.length ? nextIndex : 0);
  };

  const moveHighlightLeft = () => {
    const nextIndex = highlightedIndex - 1;
    setHighlightedIndex(nextIndex > -1 ? nextIndex : searchResults.length - 1);
  };

  const handleKeyDown = (e) => {
    if (metaTrigger(e) && e.key == 'Enter') {
      return props.onSubmit?.(e);
    }

    if (e.key == 'Enter' && highlightedIndex > -1) {
      handleSelectContact(searchResults[highlightedIndex]);
    }

    if (searchResults.length > 0) {
      if (['Tab', 'ArrowDown'].includes(e.key)) {
        moveHighlightRight();
      } else if (e.key == 'ArrowUp') {
        moveHighlightLeft();
      }
    }
  };

  const onInputChange = (e) => {
    props.onLookup(e.target.value);
    setContactSearchTerm(e.target.value);
  };

  if (props.contact) {
    if (props.selectedContactDisplay) {
      return <>{props.selectedContactDisplay}</>;
    }

    return (
      <ContactPill
        idRecord={props.contact as IDRecord}
        className={styles.pill}
        editable={!props.contactNotEditable}
        onClick={!props.contactNotEditable ? handleRemove : undefined}
      />
    );
  }

  return (
    <div className={cn(props.className)}>
      <input
        type="text"
        id="person"
        className={cn(styles.input, 'form-input-transparent')}
        ref={ref}
        value={contactSearchState.term}
        placeholder="Enter a Name, Email, LinkedIn, Twitter, or Phone..."
        onChange={onInputChange}
        onKeyDown={handleKeyDown}
        autoFocus
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        disabled={props.disabled}
      />
      <CSSTransition
        in={searchResults.length > 0}
        classNames={{
          appear: styles.suggestionsEnter,
          appearActive: styles.suggestionsEnterActive,
          appearDone: styles.suggestionsEnterDone,
          enter: styles.suggestionsEnter,
          enterActive: styles.suggestionsEnterActive,
          enterDone: styles.suggestionsEnterDone,
          exitActive: styles.suggestionsExit,
        }}
        timeout={200}
        appear
      >
        <div className={cn(styles.suggestionsContainer, 'contact-search-suggestions-container')}>
          <div className={cn(styles.suggestions, 'contact-search-suggestions')}>
            {searchResults.map((suggestion, i) => (
              <ContactPill
                key={suggestion.id}
                idRecord={suggestion}
                className={cn(
                  styles.suggestion,
                  highlightedIndex == i && styles.suggestionHighlighted,
                )}
                onClick={() => handleSelectContact(suggestion)}
              />
            ))}
          </div>
        </div>
      </CSSTransition>
    </div>
  );
}

export const ContactSearch = forwardRef(ContactSearchComponent);
